import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const titleRef = useScrollReveal();
  const leftRef = useScrollReveal();
  const rightRef = useScrollReveal();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4fm3ake56aa2r6jvei0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-24 bg-black">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={titleRef} className="text-center mb-16 scroll-reveal">
          <h2 className="text-5xl font-black text-white mb-4">Entre em Contato</h2>
          <p className="text-xl text-gray-400 font-medium">Vamos acelerar o crescimento do seu negócio juntos</p>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-200 to-amber-100 mx-auto mt-4"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div ref={leftRef} className="space-y-8 scroll-reveal-left">
            <div className="flex items-start gap-6 group cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-200 to-amber-100 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-whatsapp-line text-3xl text-black"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">WhatsApp</h3>
                <a
                  href="https://api.whatsapp.com/send?phone=5519982674838&text=Olá, gostaria de saber mais sobre os serviços da GUEPARDO AGENCIA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg text-gray-400 hover:text-amber-200 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  (19) 98267-4838
                </a>
              </div>
            </div>

            <div className="flex items-start gap-6 group cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-200 to-amber-100 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-mail-line text-3xl text-black"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">E-mail</h3>
                <a
                  href="mailto:comercial@guepardoagencia.com"
                  className="text-lg text-gray-400 hover:text-amber-200 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  comercial@guepardoagencia.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-6 group cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-200 to-amber-100 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-map-pin-line text-3xl text-black"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Endereço</h3>
                <p className="text-lg text-gray-400">
                  Rua José Paulino - Paulinia/SP
                </p>
              </div>
            </div>

            <div className="pt-8">
              <h3 className="text-2xl font-bold text-white mb-6">Siga-nos nas redes sociais</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/guepardoagencia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-amber-100 text-black hover:scale-110 transition-transform duration-300 cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-2xl"></i>
                </a>
                <a
                  href="https://api.whatsapp.com/send?phone=5519982674838&text=Olá, gostaria de saber mais sobre os serviços da GUEPARDO AGENCIA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white hover:scale-110 transition-transform duration-300 cursor-pointer"
                  aria-label="WhatsApp"
                >
                  <i className="ri-whatsapp-line text-2xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div ref={rightRef} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 shadow-xl border border-gray-800 scroll-reveal-right">
            <h3 className="text-3xl font-black text-white mb-6">Envie sua mensagem</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                  Nome completo
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-amber-200 focus:ring-2 focus:ring-amber-200/20 outline-none transition-all duration-300 text-sm"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-amber-200 focus:ring-2 focus:ring-amber-200/20 outline-none transition-all duration-300 text-sm"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-amber-200 focus:ring-2 focus:ring-amber-200/20 outline-none transition-all duration-300 text-sm"
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2">
                  Mensagem
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-amber-200 focus:ring-2 focus:ring-amber-200/20 outline-none transition-all duration-300 resize-none text-sm"
                  placeholder="Como podemos ajudar você?"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Máximo de 500 caracteres</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-200 to-amber-300 text-black font-bold py-4 px-8 rounded-full hover:shadow-xl hover:shadow-amber-200/50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-900/50 border border-green-500 text-green-300 px-4 py-3 rounded-lg">
                  Mensagem enviada com sucesso! Entraremos em contato em breve.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
                  Erro ao enviar mensagem. Por favor, tente novamente.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}